import FullBox from '@/components/full-box';
import { useSessionContext } from '@/contexts/session/context';
import { useChatContext } from '@/contexts/chat/context';
import { useColorAnalyzer } from '@/scripts/hooks/useColorAnalyzer';
import { ChatMediaType } from '@/contexts/chat/scripts/hooks/useChatMediaCall/types';
import type { RoomUser } from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import useDragMove from '../hooks/useDragMove';
import { SortableGrid, SortableGridItem } from './components/sortableGrid';
import { SubscribeStatus, type CallMemberItem } from './components/callCard/type';
import styles from './index.module.scss';
import CallCard from './components/callCard';
import CallOperate from './components/callOperate';
import DirectionIcon from './images/direction.svg?react';

const MediaModal = () => {
  const {
    session: { user },
    showProfileInfo,
  } = useSessionContext();
  const { chatMediaCall, chatMediaQuickVO, chatGroup } = useChatContext();
  const { roomUsers, joinRoomUser, quitRoomUser } = chatMediaQuickVO;
  const { dragRef, hoverOverRef, hoverLeaveRef, direction, isMouseOver, style } =
    useDragMove({
      disabled: chatMediaCall.mediaModalZoomIn,
      marginTop: 100,
      thresholdTop: 200,
    });
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridContainerInfo, setGridContainerInfo] = useState({ width: 0, height: 0 });
  const [gridCols, setGridCols] = useState<number>(0);
  const [callMembers, setCallMembers] = useState<CallMemberItem[]>([]);

  const isOnlyOne = useMemo(() => callMembers.length === 1, [callMembers]);

  const bgColor = useColorAnalyzer(chatMediaCall.chatMediaInfo?.chat?.user?.avatar, {
    format: 'rgb',
    samplingStep: 8,
    // alpha: 0.5,
  });

  const { fixedMembers, noFixedMembers, selfMember } = useMemo(() => {
    const fixed: CallMemberItem[] = [];
    const noFixed: CallMemberItem[] = [];
    const self: CallMemberItem[] = [];

    callMembers.forEach((m) => {
      if (m.fixed) {
        fixed.push(m);
      } else {
        if (m.id === user?.userId) {
          self.push(m);
        } else {
          noFixed.push(m);
        }
      }
    });
    return {
      fixedMembers: isOnlyOne ? [] : fixed,
      noFixedMembers: isOnlyOne ? self : noFixed,
      selfMember: isOnlyOne ? [] : self,
    };
  }, [callMembers, user, isOnlyOne]);

  const gridTemplateColumns = useMemo(
    () => `repeat(${chatMediaCall.mediaModalZoomIn ? gridCols : 1}, 1fr)`,
    [chatMediaCall.mediaModalZoomIn, gridCols]
  );

  const gridRows = useMemo(() => {
    if (chatMediaCall.mediaModalZoomIn && noFixedMembers.length > 2) {
      if (fixedMembers.length && noFixedMembers.length > 5) {
        return 3;
      }
      return 2;
    }
    return 1;
  }, [chatMediaCall.mediaModalZoomIn, noFixedMembers.length, fixedMembers.length]);

  const gridAutoRowsHeight = useMemo(
    () =>
      (gridContainerInfo.height -
        (chatMediaCall.mediaModalZoomIn && gridRows > 1 ? (gridRows - 1) * 10 : 0)) /
      gridRows,
    [chatMediaCall.mediaModalZoomIn, gridContainerInfo.height, gridRows]
  );

  const getGridCols = useCallback((counts: number) => {
    if (counts <= 2) return 1;
    if (counts < 6) return 2;
    return 3;
  }, []);

  const getFixedGridCols = useCallback((counts: number) => (counts <= 3 ? 1 : 2), []);

  const handleFixed = useCallback((item: any) => {
    setCallMembers((prevMembers) =>
      prevMembers.map((member) => ({
        ...member,
        fixed: item.id === member.id ? !member.fixed : false,
      }))
    );
  }, []);

  const handleSubscribe = useCallback(
    (item: CallMemberItem, status: SubscribeStatus) => {
      setCallMembers((prevMembers) =>
        prevMembers.map((member) => ({
          ...member,
          subscribeStatus: member.id === item.id ? status : member.subscribeStatus,
        }))
      );
    },
    []
  );

  const handleDragEnd = useCallback((newItems: Record<string, any>[]) => {
    setCallMembers(
      (prev) =>
        [
          ...newItems,
          ...prev.filter((item) => !newItems.find((newItem) => newItem.id === item.id)),
        ] as CallMemberItem[]
    );
  }, []);

  useLayoutEffect(() => {
    if (!chatMediaCall.openMediaModal) {
      setCallMembers([]);
    }
  }, [chatMediaCall.openMediaModal]);

  useEffect(() => {
    setGridCols(
      fixedMembers.length
        ? getFixedGridCols(callMembers.length)
        : getGridCols(callMembers.length)
    );
  }, [callMembers.length, fixedMembers.length, getFixedGridCols, getGridCols]);

  useEffect(() => {
    if (!gridContainerRef.current) return;
    const observe = new ResizeObserver(([dom]) => {
      setGridContainerInfo({
        width: (dom.target as HTMLDivElement)?.offsetWidth,
        height: (dom.target as HTMLDivElement)?.offsetHeight,
      });
    });
    observe.observe(gridContainerRef.current);
    return () => observe.disconnect();
  }, [callMembers]);

  useEffect(() => {
    if (!chatMediaCall.chatMediaInfo?.callMembers) {
      return;
    }

    const data = roomUsers?.flatMap((roomUser) => {
      let member = chatMediaCall.chatMediaInfo?.callMembers?.find(
        (member) => roomUser.id === member.userId
      );

      if (!member && chatMediaCall.chatMediaInfo?.chat?.chat_id) {
        if (chatMediaCall.chatMediaInfo?.type === ChatMediaType.GROUP) {
          const curMember = chatGroup.chatGroupState
            .getGroup(chatMediaCall.chatMediaInfo.chat.chat_id)
            ?.members.find((member) => roomUser.id === member.user_id);
          if (curMember) {
            member = { ...curMember, userId: curMember.user_id };
          }
        } else {
          member = {
            ...chatMediaCall.chatMediaInfo.chat,
            userId: chatMediaCall.chatMediaInfo.chat.chat_id,
          };
        }
      }

      const isScreenSharing = !!roomUser?.callActionMap?.screenSharing_video;

      if (!member) {
        return [];
      }

      const baseMember = {
        ...member,
        screenSharing: false,
        subscribeStatus: SubscribeStatus.DEFAULT,
        id: member.userId,
        fixed: false,
        roomUser,
      };
      if (!isScreenSharing) return [baseMember];

      const screenMember = {
        ...baseMember,
        screenSharing: true,
        id: `${member.userId}_ScreenSharing`,
      };

      return [screenMember, baseMember];
    });
    setCallMembers((prevCallMembers) => {
      const resData = data.sort((a, b) => {
        const indexA = prevCallMembers.findIndex((member) => member.id === a.id);
        const indexB = prevCallMembers.findIndex((member) => member.id === b.id);
        return indexA - indexB;
      });

      return resData
        .map((item, index) => {
          const curMemberIndex = prevCallMembers.findIndex(
            (member) => member.id === item.id
          );
          if (curMemberIndex > -1) {
            const prevMember = prevCallMembers[curMemberIndex];
            return {
              ...prevMember,
              ...item,
              fixed: prevMember.fixed,
              subscribeStatus: prevMember.subscribeStatus,
              sortIndex: 0,
            };
          } else {
            return {
              ...item,
              sortIndex: item.screenSharing ? index : 0,
            };
          }
        })
        .sort((a, b) => b.sortIndex - a.sortIndex);
    });
  }, [chatGroup.chatGroupState, chatMediaCall.chatMediaInfo, roomUsers]);

  const handleUserRoomEvent = useCallback(
    (user: RoomUser | undefined, action: 'join' | 'quit') => {
      if (
        !user?.id ||
        !chatMediaCall.chatMediaInfo?.chat?.chat_id ||
        chatMediaCall.chatMediaInfo.type !== ChatMediaType.GROUP
      ) {
        return;
      }

      const chatId = chatMediaCall.chatMediaInfo.chat.chat_id;
      const group = chatGroup.chatGroupState.getGroup(chatId);
      const member = group?.members.find((m) => m.user_id === user.id);

      if (member) {
        message.info(`${member.name} ${action === 'join' ? 'join' : 'exit'} call`);
      }
    },
    [chatGroup.chatGroupState, chatMediaCall.chatMediaInfo]
  );

  useEffect(() => {
    handleUserRoomEvent(joinRoomUser, 'join');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinRoomUser]);

  useEffect(() => {
    handleUserRoomEvent(quitRoomUser, 'quit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quitRoomUser]);

  const renderCallCards = (members: CallMemberItem[]) =>
    members.map((member) => (
      <CallCard
        key={member.id}
        mediaModalZoomIn={chatMediaCall.mediaModalZoomIn}
        user={user!}
        member={member}
        onFixed={() => handleFixed(member)}
        onSubscribe={(status) => handleSubscribe(member, status)}
      />
    ));

  return (
    <div
      id="mediaModal"
      ref={dragRef}
      style={{
        ...style,
        zIndex: chatMediaCall.mediaModalZoomIn && showProfileInfo.show ? 999 : 2000,
      }}
      className={cls(
        styles.mediaCallModal,
        chatMediaCall.openMediaModal ? styles.open : styles.close,
        !chatMediaCall.mediaModalZoomIn && styles.zoomOut
      )}
    >
      <FullBox
        ref={hoverLeaveRef}
        className={cls(
          styles.mediaCallModalWrapper,
          !chatMediaCall.mediaModalZoomIn && styles.zoomOut
        )}
        style={{
          background: chatMediaCall.mediaModalZoomIn ? '' : bgColor,
        }}
        column
        rearChildren={<CallOperate />}
      >
        <div
          className={cls(
            styles.mediaCallContainer,
            !chatMediaCall.mediaModalZoomIn && styles.zoomOut
          )}
        >
          <div className={cls(styles.left, !fixedMembers.length && styles.unLeft)}>
            {renderCallCards(fixedMembers)}
          </div>

          <div
            className={cls('mediaModalRight', styles.right)}
            hidden={!chatMediaCall.mediaModalZoomIn && !!fixedMembers.length}
            ref={gridContainerRef}
          >
            <SortableGrid onDragEnd={handleDragEnd} items={noFixedMembers}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  gridAutoRows: gridAutoRowsHeight + 'px',
                  gap: 10,
                }}
              >
                {noFixedMembers.map((member, index) => (
                  <SortableGridItem
                    id={member.id}
                    key={member.id}
                    disabled={
                      !chatMediaCall.mediaModalZoomIn || noFixedMembers.length === 1
                    }
                    hidden={!chatMediaCall.mediaModalZoomIn && index !== 0}
                  >
                    {(isDragging) => {
                      return (
                        <CallCard
                          hasFixed={!!fixedMembers.length}
                          rowsHeight={gridAutoRowsHeight}
                          index={index}
                          isDragging={isDragging}
                          key={member.id}
                          mediaModalZoomIn={chatMediaCall.mediaModalZoomIn}
                          user={user!}
                          member={member}
                          onFixed={() => handleFixed(member)}
                          onSubscribe={(status) => handleSubscribe(member, status)}
                          isOnlyOne={isOnlyOne}
                        />
                      );
                    }}
                  </SortableGridItem>
                ))}
              </div>
            </SortableGrid>
          </div>
          {renderCallCards(selfMember)}
        </div>
      </FullBox>
      <div
        ref={hoverOverRef}
        className={cls(styles.mediaCallLabel, {
          [styles.left]: direction.isLeft && isMouseOver,
          [styles.right]: direction.isRight && isMouseOver,
        })}
      >
        <DirectionIcon />
      </div>
    </div>
  );
};

export default MediaModal;
